import React, { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { ClientPortalNavigation } from "../ClientPortalNavigation";
import CustomDropdown from "components/customDropdown/CustomDropdown";
import CustomDropdownOption from "components/customDropdown/CustomDropdownOption";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar, faXmark } from "@fortawesome/pro-regular-svg-icons";
import { ButtonNew as Button } from "components/buttonNew";
import Card from "components/card/Card";
import Input from "components/form/input/Input";
import FormLayout from "components/form/FormLayout";
import { Select } from "components/select/index";
import Checkbox from "components/form/checkbox/Checkbox";
import { clientPortalUpdate } from "redux/actions/clientPortal";
import { MOBILE_WIDTH } from "helpers/ScreenSizes";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import "./reserveBooking.scss";
import { showError } from "redux/actions/ui";
import { capitalize, displayCurrency, getCreditCardIcon, handleChangeEventInput } from "helpers/Helpers";
import CardSection from "components/cardSection/CardSection";
import { StatusCode } from "api/protocols";
import { useElements, useStripe } from "@stripe/react-stripe-js";
import { GetCustomerAddressLines } from "api/rpc/2024-04/customer/customer";
import TextField from "components/form/textField/TextField";
import { useAccommodationReservation } from "hooks/accommodation/useAccommodationReservation";

interface IParams {
  clientShortName: string;
  facilityShortName: string;
}

const provinces = [
  { id: 1, name: "Alberta" },
  { id: 2, name: "British Columbia" },
  { id: 3, name: "Manitoba" },
  { id: 4, name: "New Brunswick" },
  { id: 5, name: "Newfoundland and Labrador" },
  { id: 6, name: "Northwest Territories" },
  { id: 7, name: "Nova Scotia" },
  { id: 8, name: "Nunavut" },
  { id: 9, name: "Ontario" },
  { id: 10, name: "Prince Edward Island" },
  { id: 11, name: "Quebec" },
  { id: 12, name: "Saskatchewan" },
  { id: 13, name: "Yukon" },
];

export default function ReserveBooking() {
  const { t } = useTranslation();
  const history = useHistory();
  const { clientShortName, facilityShortName } = useParams<IParams>();
  const dispatch = useAppDispatch();
  const { clientPortalStore } = useAppSelector(store => store);
  const windowSize = useWindowSize();
  const stripe = useStripe();
  const elements = useElements();

  const {
    customerPaymentMethods,
    selectedPaymentMethod,
    billingAddress,
    newReservationState,
    setSelectedPaymentMethod,
    setBillingAddress,
    handleCardInputChange,
    handleBookReservation: bookReservation,
    setNewReservationState,
  } = useAccommodationReservation({
    isAdmin: false,
    facilityId: clientPortalStore?.selectedFacility?.id,
  });

  useEffect(() => {
    if (!clientPortalStore?.selectedFacility) {
      history.push(`/portal/${clientShortName}/accommodations/${facilityShortName ?? ""}`);
    }
  }, [clientPortalStore?.selectedFacility, clientShortName, facilityShortName, history]);

  useEffect(() => {
    void loadCustomerAddressLines();
  }, []);

  const [acknowledgements, setAcknowledgements] = useState({
    newsletter: false,
    terms: false,
  });

  const hasPaymentMethod = newReservationState?.newCreditCardActive
    ? Boolean(
        newReservationState.elements?.cardNumber &&
          newReservationState.elements?.cardExpiry &&
          newReservationState.elements?.cardCvc,
      )
    : Boolean(selectedPaymentMethod);

  const [addNoteState, setAddNoteState] = useState({
    showNote: false,
    note: "",
  });

  const [customerAddressLine, setCustomerAddressLine] = useState(undefined);

  const facilities = useMemo(() => {
    return clientPortalStore?.client?.facilities || [];
  }, [clientPortalStore?.client?.facilities]);

  const selectedFacilityElement = (
    <div className={"accommodations-select-unit-facility-container"}>
      {clientPortalStore?.client?.facilities?.length > 1 && clientPortalStore?.selectedFacility?.logo_source && (
        <div>
          <img
            className="accommodations-select-unit-home-course-logo"
            src={clientPortalStore?.selectedFacility?.logo_source}
            alt="Facility Logo"
          />
        </div>
      )}
      <div>
        <div className="accommodations-select-unit-home-course-name">
          {clientPortalStore?.selectedFacility?.long_name}
        </div>
        {clientPortalStore?.selectedFacility && (
          <div className="accommodations-select-unit-secondary-information">
            {`${clientPortalStore?.selectedFacility?.address_line_1}, ${clientPortalStore?.selectedFacility?.city}, ${clientPortalStore?.selectedFacility?.province_name}, ${clientPortalStore?.selectedFacility?.postal}`}
          </div>
        )}
      </div>
    </div>
  );

  function handleFacilityChange(id: number) {
    const foundFacility = clientPortalStore?.client?.facilities?.find(facility => facility?.id === id);
    if (foundFacility) {
      dispatch(clientPortalUpdate({ selectedFacility: foundFacility }));
    }
  }

  async function loadCustomerAddressLines() {
    const addressLinesRes = await GetCustomerAddressLines({ customer_id: clientPortalStore?.user?.id }, true);

    if (addressLinesRes?.status === StatusCode.OK) {
      const address = Array.isArray(addressLinesRes?.data) ? addressLinesRes?.data?.[0] : addressLinesRes?.data;
      setCustomerAddressLine(address);

      if (address) {
        const provinceObj = provinces?.find(p => p?.name === address?.province_name);
        setBillingAddress({
          address: address?.address_line_1 || "",
          city: address?.city || "",
          country: address?.country_name === "Canada" ? 1 : 1,
          postalCode: address?.postal || "",
          province: provinceObj ? provinceObj?.id : undefined,
        });
      }
    } else {
      dispatch(showError("Error loading address lines"));
    }
  }

  async function handleBook() {
    if (!acknowledgements.terms || !hasPaymentMethod) {
      return;
    }

    const isAddressSame =
      customerAddressLine &&
      billingAddress?.address === customerAddressLine?.address_line_1 &&
      billingAddress?.city === customerAddressLine?.city &&
      billingAddress?.postalCode === customerAddressLine?.postal &&
      provinces?.find(p => p.id === billingAddress?.province)?.name === customerAddressLine?.province_name &&
      (billingAddress?.country === 1 ? "Canada" : "USA") === customerAddressLine?.country_name;

    const billingAddressParams = isAddressSame
      ? { billing_address_id: customerAddressLine?.id }
      : {
          billing_address: {
            address_line_1: billingAddress?.address,
            city: billingAddress?.city,
            postal: billingAddress?.postalCode,
            country_id: billingAddress?.country,
            province_id: billingAddress?.province,
          },
        };

    await bookReservation(
      {
        ...billingAddressParams,
        terms_accepted: acknowledgements.terms,
        newsletter_accepted: acknowledgements.newsletter,
        note: addNoteState.note,
      },
      stripe,
      elements,
      () =>
        history.push(
          `/portal/${clientShortName}/accommodations/${clientPortalStore?.selectedFacility?.short_name ?? ""}`,
        ),
    );
  }

  return (
    <div>
      <ClientPortalNavigation showNavOptions />

      <div className="accommodations-select-unit-dropdown-facility-container">
        {clientPortalStore?.client?.facilities?.length > 1 ? (
          <CustomDropdown
            parentElement={selectedFacilityElement}
            onChange={handleFacilityChange}
            defaultValue={clientPortalStore?.selectedFacility?.id}
            searchPlacerHolder="Search courses..."
          >
            {facilities?.map((facility, index) => (
              <CustomDropdownOption value={facility.id} key={index}>
                <div className="accommodations-select-unit-facility-option-container">
                  <img
                    className="accommodations-select-unit-facility-option-facility-logo"
                    src={facility?.logo_source}
                    alt="Facility Logo"
                  />
                  <p className="accommodations-select-unit-facility-option-facility-name">{facility?.long_name}</p>
                  {windowSize.width > MOBILE_WIDTH && (
                    <p className="accommodations-select-unit-facility-option-facility-address">{facility?.city}</p>
                  )}
                  {facility?.favourite && (
                    <FontAwesomeIcon
                      size="1x"
                      fixedWidth
                      className="accommodations-select-unit-facility-option-favourite"
                      icon={faStar}
                    />
                  )}
                </div>
              </CustomDropdownOption>
            ))}
          </CustomDropdown>
        ) : (
          selectedFacilityElement
        )}

        <div>
          <Button type="secondary" size="small" onClick={() => window.open(`/tee-on/portal/loyalty-program`, "_blank")}>
            <FontAwesomeIcon icon={faStar} />
            <p className="ml-2">{t("Join loyalty program")}</p>
          </Button>
        </div>
      </div>

      <div className="reserve-booking-container">
        <div className="reserve-booking-header">
          <div className="reserve-booking-header-breadcrumb-container">
            <span
              onClick={() =>
                history.push(
                  `/portal/${clientShortName}/accommodations/${clientPortalStore?.selectedFacility?.short_name ?? ""}`,
                )
              }
              className="reserve-booking-header-breadcrumb-item"
            >
              {t("Accommodations")}
            </span>
            <span className="reserve-booking-header-breadcrumb-separator"> &gt; </span>
            <span
              onClick={() =>
                history.push(
                  `/portal/${clientShortName}/accommodations/${
                    clientPortalStore?.selectedFacility?.short_name ?? ""
                  }/select-unit`,
                )
              }
              className="reserve-booking-header-breadcrumb-item"
            >
              {t("Select a room")}
            </span>
            <span className="reserve-booking-header-breadcrumb-separator"> &gt; </span>
            <span className="reserve-booking-header-breadcrumb-active">{t("Reserve your booking")}</span>
          </div>

          <h1 className="reserve-booking-header-title">{t("Reserve your booking")}</h1>
          <p className="reserve-booking-header-secondary-information mt-2">
            {clientPortalStore?.accommodationReservation?.selectedUnitType?.title || "Unit"} •{" "}
            {clientPortalStore?.accommodationReservation?.adults || 0} adult
            {(clientPortalStore?.accommodationReservation?.adults || 0) !== 1 ? "s" : ""}
            {(clientPortalStore?.accommodationReservation?.children || 0) > 0 &&
              `, ${clientPortalStore?.accommodationReservation?.children} child${
                clientPortalStore?.accommodationReservation?.children !== 1 ? "ren" : ""
              }`}
          </p>
          <hr style={{ marginBottom: "0px" }} />
        </div>

        <div className="reserve-booking-content">
          <div className="reserve-booking-left-column">
            <Card title={t("Contact information")}>
              <div className="pl-4 pr-4">
                <hr style={{ marginBottom: "0px" }} />
              </div>
              <div className="p-4">
                <FormLayout>
                  <FormLayout.Group>
                    <Input label={t("First name")} disabled value={clientPortalStore?.user?.first_name} />
                    <Input label={t("Last name")} disabled value={clientPortalStore?.user?.last_name} />
                  </FormLayout.Group>
                  <FormLayout.Group>
                    <Input label={t("Email")} disabled value={clientPortalStore?.user?.email} />
                    <Input label={t("Phone number")} disabled value={clientPortalStore?.user?.phone} />
                  </FormLayout.Group>
                </FormLayout>
              </div>
            </Card>

            <Card
              title={t("Payment details")}
              titleActions={
                !newReservationState?.newCreditCardActive
                  ? []
                  : [
                      {
                        action: () => setNewReservationState(p => ({ ...p, newCreditCardActive: false })),
                        content: <FontAwesomeIcon icon={faXmark} />,
                      },
                    ]
              }
            >
              <div className="pl-4 pr-4">
                <hr style={{ marginBottom: "0px" }} />
              </div>
              <div className="pl-4 pr-4 pt-3">
                {newReservationState?.newCreditCardActive ? (
                  <div>
                    <Input
                      id="newCreditCardCustomerName"
                      value={newReservationState?.newCreditCardCustomerName}
                      label={t("Cardholder name")}
                      onChange={e => handleChangeEventInput(e, newReservationState, setNewReservationState)}
                      placeholder={t("Enter full name of cardholder")}
                    />
                    <div className="mt-4">
                      <CardSection type="one-row" onChange={handleCardInputChange} />
                    </div>
                  </div>
                ) : (
                  <>
                    {customerPaymentMethods?.length > 0 && (
                      <div className="mt-2">
                        {customerPaymentMethods?.map((paymentMethod, index) => (
                          <div
                            className={`payment-card-option ${
                              selectedPaymentMethod?.id === paymentMethod.id ? "selected" : ""
                            }`}
                            onClick={() =>
                              setSelectedPaymentMethod(
                                selectedPaymentMethod?.id === paymentMethod.id ? undefined : paymentMethod,
                              )
                            }
                            key={index}
                          >
                            <FontAwesomeIcon
                              icon={getCreditCardIcon(paymentMethod.brand)}
                              size="2x"
                              className="card-icon"
                            />
                            <div>
                              <div className="font-semibold">
                                {capitalize(paymentMethod.brand) + " ending in " + paymentMethod.last4}
                              </div>
                              <div className="text-sm text-gray-500">
                                {t("Expiry ") + paymentMethod.exp_month + "/" + paymentMethod.exp_year}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}

                {!newReservationState?.newCreditCardActive && (
                  <div className="mt-4 mb-2">
                    <Button
                      type="link"
                      onClick={() => setNewReservationState(p => ({ ...p, newCreditCardActive: true }))}
                    >
                      + Add new payment method
                    </Button>
                  </div>
                )}

                <div className="mt-5">
                  <h3 className="font-semibold mb-2">{t("Billing address")}</h3>
                  <FormLayout>
                    <FormLayout.Group>
                      <Input
                        label={t("Address")}
                        placeholder={t("Address line 1")}
                        value={billingAddress?.address}
                        onChange={e => setBillingAddress({ ...billingAddress, address: e.target.value })}
                      />
                    </FormLayout.Group>
                    <FormLayout.Group>
                      <Select
                        label={t("Country")}
                        onChange={(val: number) => setBillingAddress(p => ({ ...p, country: val }))}
                        defaultValue={billingAddress?.country}
                        placeholder={t("Select country")}
                      >
                        <Select.Option value={1} name="Canada">
                          Canada
                        </Select.Option>
                      </Select>
                      <Input
                        label={t("City")}
                        value={billingAddress?.city}
                        onChange={e => setBillingAddress({ ...billingAddress, city: e.target.value })}
                        placeholder={t("Enter city")}
                      />
                    </FormLayout.Group>
                    <FormLayout.Group>
                      <Select
                        label={t("Province")}
                        onChange={(val: number) => setBillingAddress(p => ({ ...p, province: val }))}
                        defaultValue={billingAddress?.province}
                        placeholder={t("Select province")}
                      >
                        {provinces.map(p => (
                          <Select.Option key={p.id} value={p.id} name={p.name}>
                            {p.name}
                          </Select.Option>
                        ))}
                      </Select>
                      <Input
                        label={t("Postal Code")}
                        value={billingAddress?.postalCode}
                        onChange={e => setBillingAddress({ ...billingAddress, postalCode: e.target.value })}
                        placeholder={t("Enter postal code")}
                      />
                    </FormLayout.Group>
                  </FormLayout>
                </div>
              </div>
            </Card>

            <div className="mt-4">
              <Button
                type="link"
                onClick={() => setAddNoteState({ ...addNoteState, showNote: !addNoteState.showNote })}
              >
                {addNoteState.showNote ? t("- Remove reservation note") : t("+ Add reservation note")}
              </Button>
              {addNoteState.showNote && (
                <div className="mt-3">
                  <TextField
                    value={addNoteState.note}
                    onChange={e => setAddNoteState({ ...addNoteState, note: e.target.value })}
                    placeholder={t("Enter any special requests or information for your stay...")}
                  />
                </div>
              )}
            </div>

            <div className="mt-5 mb-5">
              <Checkbox
                id="newsletter"
                size="small"
                checked={acknowledgements.newsletter}
                onChange={() => setAcknowledgements({ ...acknowledgements, newsletter: !acknowledgements.newsletter })}
                label={t("Please send me newsletters, updates and promotions from GolfNorth Properties.")}
              />
              <div className="mt-3">
                <Checkbox
                  id="terms"
                  size="small"
                  checked={acknowledgements.terms}
                  onChange={() => setAcknowledgements({ ...acknowledgements, terms: !acknowledgements.terms })}
                  label={
                    <span>
                      {t("I have read and agree to the ")}
                      <a href="#" className="underline">
                        {t("Booking Policy")}
                      </a>
                      {t(" and ")}
                      <a href="#" className="underline">
                        {t("Privacy Policy")}
                      </a>
                      .
                    </span>
                  }
                />
              </div>
            </div>
          </div>

          <div className="reserve-booking-right-column">
            <Card title={t("Order summary")}>
              <div className="pl-4 pr-4">
                <hr style={{ marginBottom: "0px" }} />
              </div>
              <div className="p-4">
                <div className="flex justify-between mb-4">
                  <div>
                    <p className="font-semibold">
                      {clientPortalStore?.accommodationReservation?.nights} {t("night")}
                      {(clientPortalStore?.accommodationReservation?.nights || 0) !== 1 ? "s" : ""}
                    </p>
                    <p className="text-sm text-gray-500">
                      {new Date(clientPortalStore?.accommodationReservation?.arrival_date || "").toLocaleDateString()} -{" "}
                      {new Date(clientPortalStore?.accommodationReservation?.departure_date || "").toLocaleDateString()}
                    </p>
                  </div>
                  <p className="font-semibold">
                    {displayCurrency("CAD", clientPortalStore?.accommodationReservation?.subtotalPrice || 0)}
                  </p>
                </div>

                <div className="flex justify-between mb-4">
                  <p>{t("Taxes")}</p>
                  <p>{displayCurrency("CAD", clientPortalStore?.accommodationReservation?.taxAmount || 0)}</p>
                </div>

                <hr className="mb-4" />

                <div className="flex justify-between items-center mb-6">
                  <p className="text-xl font-bold">{t("Total")}</p>
                  <p className="text-xl font-bold">
                    {displayCurrency("CAD", clientPortalStore?.accommodationReservation?.totalPrice || 0)}
                  </p>
                </div>

                <Button
                  disabled={!acknowledgements.terms || !hasPaymentMethod}
                  className="w-full justify-center"
                  onClick={handleBook}
                >
                  {t("Book reservation")}
                </Button>
                <p className="text-xs text-center text-gray-400 mt-4">
                  {t(
                    "By clicking 'Book reservation' you agree to the automatic charge for any potential cancellation fees as per the booking policy.",
                  )}
                </p>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
